/** Estimated label box + leader geometry for plan-view sensor cards. */

export type PlanSensorLabelLayout = {
  /** Label centre offset from map anchor / pin tip (px). */
  centerOffsetX: number;
  centerOffsetY: number;
  /** Polyline from pin tip (0,0) toward the label attach point. */
  connectorPoints: { x: number; y: number }[];
};

type Rect = { x: number; y: number; w: number; h: number };

type SensorPoint = { id: string; x: number; y: number; name: string };

export const LABEL_HEIGHT = 64;
const DOT_RADIUS = 5;
const MIN_LINE = 10;
const MAX_EXTRA_LINE = 100;
const LINE_STEP = 16;
/** Shrink collision boxes — only react when labels would meaningfully overlap. */
const OVERLAP_INSET = 16;
const H_SHIFT_STEPS = [0, -48, 48, -96, 96];

function estimateLabelWidth(name: string): number {
  const min = 56;
  const max = 200;
  const charW = 5.5;
  return Math.min(max, Math.max(min, name.length * charW + 16));
}

function anchorPx(sensor: SensorPoint, canvasW: number, canvasH: number) {
  return { ax: sensor.x * canvasW, ay: sensor.y * canvasH };
}

function rectsOverlap(a: Rect, b: Rect, inset: number): boolean {
  const shrink = (r: Rect): Rect | null => {
    const x = r.x + inset;
    const y = r.y + inset;
    const w = r.w - inset * 2;
    const h = r.h - inset * 2;
    if (w <= 0 || h <= 0) return null;
    return { x, y, w, h };
  };
  const sa = shrink(a);
  const sb = shrink(b);
  if (!sa || !sb) return false;
  return !(
    sa.x + sa.w <= sb.x ||
    sb.x + sb.w <= sa.x ||
    sa.y + sa.h <= sb.y ||
    sb.y + sb.h <= sa.y
  );
}

type Candidate = PlanSensorLabelLayout & { rect: Rect };

function rectFromCenter(
  ax: number,
  ay: number,
  centerOffsetX: number,
  centerOffsetY: number,
  labelW: number,
): Rect {
  const cx = ax + centerOffsetX;
  const cy = ay + centerOffsetY;
  return { x: cx - labelW / 2, y: cy - LABEL_HEIGHT / 2, w: labelW, h: LABEL_HEIGHT };
}

function attachLocalY(centerOffsetY: number): number {
  return centerOffsetY + LABEL_HEIGHT / 2;
}

/** Straight or bent leader above the pin tip. */
function candidateAbove(
  ax: number,
  ay: number,
  labelW: number,
  extraLine: number,
  shiftX: number,
): Candidate {
  const lineLen = MIN_LINE + extraLine;
  const centerOffsetX = shiftX;
  const centerOffsetY = -(DOT_RADIUS + lineLen + LABEL_HEIGHT / 2);
  const attachY = attachLocalY(centerOffsetY);
  const connectorPoints =
    shiftX === 0
      ? [
          { x: 0, y: 0 },
          { x: 0, y: attachY },
        ]
      : [
          { x: 0, y: 0 },
          { x: 0, y: attachY * 0.55 },
          { x: shiftX, y: attachY * 0.55 },
          { x: shiftX, y: attachY },
        ];
  return {
    centerOffsetX,
    centerOffsetY,
    connectorPoints,
    rect: rectFromCenter(ax, ay, centerOffsetX, centerOffsetY, labelW),
  };
}

/** L-shaped leader: vertical segment then horizontal to label. */
function candidateElbow(
  ax: number,
  ay: number,
  labelW: number,
  vertical: number,
  shiftX: number,
): Candidate {
  const centerOffsetX = shiftX;
  const centerOffsetY = -(DOT_RADIUS + vertical + LABEL_HEIGHT / 2);
  const kneeY = -(DOT_RADIUS + vertical);
  const attachY = attachLocalY(centerOffsetY);
  const connectorPoints = [
    { x: 0, y: 0 },
    { x: 0, y: kneeY },
    { x: shiftX, y: kneeY },
    { x: shiftX, y: attachY },
  ];
  return {
    centerOffsetX,
    centerOffsetY,
    connectorPoints,
    rect: rectFromCenter(ax, ay, centerOffsetX, centerOffsetY, labelW),
  };
}

/** Label to the left or right with a horizontal leader. */
function candidateSide(
  ax: number,
  ay: number,
  labelW: number,
  side: 'left' | 'right',
  gap: number,
): Candidate {
  const centerOffsetY = 0;
  const centerOffsetX =
    side === 'left'
      ? -(DOT_RADIUS + gap + labelW / 2)
      : DOT_RADIUS + gap + labelW / 2;
  const attachX =
    side === 'left'
      ? centerOffsetX + labelW / 2
      : centerOffsetX - labelW / 2;
  const connectorPoints = [
    { x: 0, y: 0 },
    { x: attachX, y: 0 },
  ];
  return {
    centerOffsetX,
    centerOffsetY,
    connectorPoints,
    rect: rectFromCenter(ax, ay, centerOffsetX, centerOffsetY, labelW),
  };
}

function candidateBelow(
  ax: number,
  ay: number,
  labelW: number,
  extraLine: number,
  shiftX: number,
): Candidate {
  const lineLen = MIN_LINE + extraLine;
  const centerOffsetX = shiftX;
  const centerOffsetY = DOT_RADIUS + lineLen + LABEL_HEIGHT / 2;
  const attachY = attachLocalY(centerOffsetY);
  const connectorPoints =
    shiftX === 0
      ? [
          { x: 0, y: 0 },
          { x: 0, y: attachY },
        ]
      : [
          { x: 0, y: 0 },
          { x: 0, y: attachY * 0.55 },
          { x: shiftX, y: attachY * 0.55 },
          { x: shiftX, y: attachY },
        ];
  return {
    centerOffsetX,
    centerOffsetY,
    connectorPoints,
    rect: rectFromCenter(ax, ay, centerOffsetX, centerOffsetY, labelW),
  };
}

function defaultCandidate(ax: number, ay: number, labelW: number): Candidate {
  return candidateAbove(ax, ay, labelW, 0, 0);
}

/** Prefer longer straight leaders before bends or side placements. */
function buildCandidates(ax: number, ay: number, labelW: number): Candidate[] {
  const out: Candidate[] = [];
  for (let extra = 0; extra <= MAX_EXTRA_LINE; extra += LINE_STEP) {
    out.push(candidateAbove(ax, ay, labelW, extra, 0));
  }
  for (let extra = LINE_STEP; extra <= MAX_EXTRA_LINE; extra += LINE_STEP) {
    for (const shiftX of H_SHIFT_STEPS.filter((s) => s !== 0)) {
      out.push(candidateAbove(ax, ay, labelW, extra, shiftX));
    }
  }
  for (const vertical of [24, 40, 56, 72]) {
    for (const shiftX of H_SHIFT_STEPS.filter((s) => s !== 0)) {
      out.push(candidateElbow(ax, ay, labelW, vertical, shiftX));
    }
  }
  for (const gap of [16, 32]) {
    out.push(candidateSide(ax, ay, labelW, 'left', gap));
    out.push(candidateSide(ax, ay, labelW, 'right', gap));
  }
  for (let extra = LINE_STEP; extra <= 48; extra += LINE_STEP) {
    out.push(candidateBelow(ax, ay, labelW, extra, 0));
  }
  return out;
}

function neighborCount(
  sensor: SensorPoint,
  all: SensorPoint[],
  canvasW: number,
  canvasH: number,
): number {
  const { ax, ay } = anchorPx(sensor, canvasW, canvasH);
  const threshold = Math.min(canvasW, canvasH) * 0.07;
  let n = 0;
  for (const other of all) {
    if (other.id === sensor.id) continue;
    const { ax: ox, ay: oy } = anchorPx(other, canvasW, canvasH);
    if (Math.hypot(ax - ox, ay - oy) < threshold) n++;
  }
  return n;
}

function isClipped(rect: Rect, canvasW: number, canvasH: number): boolean {
  return (
    rect.x < 0 ||
    rect.y < 0 ||
    rect.x + rect.w > canvasW ||
    rect.y + rect.h > canvasH
  );
}

export const DEFAULT_PLAN_LABEL_LAYOUT: PlanSensorLabelLayout = {
  centerOffsetX: 0,
  centerOffsetY: -(DOT_RADIUS + MIN_LINE + LABEL_HEIGHT / 2),
  connectorPoints: [
    { x: 0, y: 0 },
    { x: 0, y: -(DOT_RADIUS + MIN_LINE) },
  ],
};

/** Build layout from saved/manual label centre offsets (straight leader to pin). */
export function layoutFromManualOffset(
  centerOffsetX: number,
  centerOffsetY: number,
): PlanSensorLabelLayout {
  const attachY = centerOffsetY + LABEL_HEIGHT / 2;
  return {
    centerOffsetX,
    centerOffsetY,
    connectorPoints: [
      { x: 0, y: 0 },
      { x: centerOffsetX, y: attachY },
    ],
  };
}

export function hasManualLabelOffset(sensor: {
  labelCenterOffsetX?: number | null;
  labelCenterOffsetY?: number | null;
}): boolean {
  return (
    sensor.labelCenterOffsetX != null &&
    sensor.labelCenterOffsetY != null &&
    Number.isFinite(sensor.labelCenterOffsetX) &&
    Number.isFinite(sensor.labelCenterOffsetY)
  );
}

/**
 * Assign label positions for sensors on the plan canvas.
 * Default short leader above the pin; only adjust when labels would clearly overlap.
 */
export function computePlanLabelLayouts(
  sensors: SensorPoint[],
  canvasW: number,
  canvasH: number,
): Map<string, PlanSensorLabelLayout> {
  const result = new Map<string, PlanSensorLabelLayout>();
  if (!sensors.length || canvasW <= 0 || canvasH <= 0) return result;

  const defaults = new Map<string, Candidate>();
  for (const sensor of sensors) {
    const { ax, ay } = anchorPx(sensor, canvasW, canvasH);
    const labelW = estimateLabelWidth(sensor.name);
    defaults.set(sensor.id, defaultCandidate(ax, ay, labelW));
  }

  const needsAdjust = new Set<string>();
  for (let i = 0; i < sensors.length; i++) {
    const a = sensors[i];
    const rectA = defaults.get(a.id)!.rect;
    for (let j = i + 1; j < sensors.length; j++) {
      const b = sensors[j];
      const rectB = defaults.get(b.id)!.rect;
      if (rectsOverlap(rectA, rectB, OVERLAP_INSET)) {
        needsAdjust.add(a.id);
        needsAdjust.add(b.id);
      }
    }
  }

  const placedRects: Rect[] = [];
  const sorted = [...sensors].sort(
    (a, b) =>
      (needsAdjust.has(b.id) ? 1 : 0) - (needsAdjust.has(a.id) ? 1 : 0) ||
      neighborCount(b, sensors, canvasW, canvasH) -
        neighborCount(a, sensors, canvasW, canvasH) ||
      a.y - b.y ||
      a.x - b.x,
  );

  for (const sensor of sorted) {
    const { ax, ay } = anchorPx(sensor, canvasW, canvasH);
    const labelW = estimateLabelWidth(sensor.name);

    if (!needsAdjust.has(sensor.id)) {
      const def = defaults.get(sensor.id)!;
      placedRects.push(def.rect);
      result.set(sensor.id, {
        centerOffsetX: def.centerOffsetX,
        centerOffsetY: def.centerOffsetY,
        connectorPoints: def.connectorPoints,
      });
      continue;
    }

    const candidates = buildCandidates(ax, ay, labelW);
    let chosen: Candidate | null = null;
    for (const c of candidates) {
      if (isClipped(c.rect, canvasW, canvasH)) continue;
      if (placedRects.some((r) => rectsOverlap(c.rect, r, OVERLAP_INSET))) continue;
      chosen = c;
      break;
    }

    if (!chosen) {
      chosen = candidateAbove(ax, ay, labelW, MAX_EXTRA_LINE, 0);
    }

    placedRects.push(chosen.rect);
    result.set(sensor.id, {
      centerOffsetX: chosen.centerOffsetX,
      centerOffsetY: chosen.centerOffsetY,
      connectorPoints: chosen.connectorPoints,
    });
  }

  return result;
}
